import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../../services/api'

/**
 * BillingWorkflow — month-over-month invoice lifecycle for admin.
 *
 * Flow a Jarix admin follows each month:
 *   1. Pick the period (current month by default).
 *   2. See every tenant's billing row with a state badge:
 *        pending → sent_to_finance → invoiced → paid / overdue / void
 *   3. If rows are missing fiscal data, the "Enviar a Finanzas"
 *      button is disabled until they're fixed (prevents sending
 *      garbage to the ERP team).
 *   4. "Enviar a Finanzas" calls the worker which emails josehuenuman
 *      at jarix.io with an HTML summary + NetSuite-ready CSV, and
 *      flips the rows to sent_to_finance.
 *   5. Admin marks rows as invoiced (with NetSuite invoice number)
 *      or paid (with date) as Finanzas reports back.
 *
 * UX is optimized for the operational task — keyboard + inline edits
 * would be even better but dialog-based flow is enough for MVP.
 */

// Sprint 6: a "row" is now a full invoice with nested line items.
// Managed-plan tenants still fill billable_users/plan/price_per_user
// from the managed_plan line (computed backend-side) so the existing
// top-level UI keeps working. Client-only invoices (PS-only) will
// have billable_users = 0 and plan = '—' — the lines are where the
// real content lives for those.
export interface InvoiceLineItem {
  id: string
  lineType: 'managed_plan' | 'client_service' | 'adjustment'
  sourceId: string | null
  description: string
  quantity: number
  unitPrice: number
  discountPercent: number
  lineTotal: number
  unitCost: number | null
  metadata: Record<string, unknown> | null
  sortOrder: number
}

interface BillingRow {
  id: string
  customerType?: 'tenant' | 'client'
  tenant_id: string | null
  client_id?: string | null
  tenant_name: string           // really "customer_name" — kept for compat
  tenant_slug: string
  legal_name: string | null
  tax_id: string | null
  billing_emails: string | null
  payment_terms: string
  billable_users: number
  plan: string
  price_per_user: number
  monthly_total: number
  invoice_status: string
  invoice_number: string | null
  invoice_date: string | null
  due_date: string | null
  paid_date: string | null
  sent_to_finance_at: string | null
  purchase_order: string | null
  notes_internal: string | null
  invoice_pdf_url: string | null
  primary_color: string
  lineItems?: InvoiceLineItem[]
}

interface BillingWorkflowProps {
  onCaptureSnapshot: (year?: number, month?: number) => Promise<void>
  snapshotLoading: boolean
}

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                     'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

type InvoiceStatus = 'pending' | 'sent_to_finance' | 'invoiced' | 'paid' | 'overdue' | 'void'

const STATUS_META: Record<InvoiceStatus, { label: string; bg: string; text: string; border: string }> = {
  pending:         { label: 'Pendiente',   bg: 'bg-gray-600/20',   text: 'text-gray-300',   border: 'border-gray-500/40' },
  sent_to_finance: { label: 'Enviado FI',  bg: 'bg-blue-500/20',   text: 'text-blue-300',   border: 'border-blue-500/40' },
  invoiced:        { label: 'Facturada',   bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/40' },
  paid:            { label: 'Pagada',      bg: 'bg-green-500/20',  text: 'text-green-300',  border: 'border-green-500/40' },
  overdue:         { label: 'Vencida',     bg: 'bg-red-500/20',    text: 'text-red-300',    border: 'border-red-500/40' },
  void:            { label: 'Anulada',     bg: 'bg-gray-700/40',   text: 'text-gray-500',   border: 'border-gray-600/40' },
}

function isRowComplete(r: BillingRow): boolean {
  if (!r.legal_name || !r.tax_id) return false
  try {
    const parsed = JSON.parse(r.billing_emails || '[]')
    return Array.isArray(parsed) && parsed.length > 0
  } catch {
    return false
  }
}

export default function BillingWorkflow({ onCaptureSnapshot, snapshotLoading }: BillingWorkflowProps) {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [rows, setRows] = useState<BillingRow[]>([])
  const [total, setTotal] = useState(0)
  const [byStatus, setByStatus] = useState<Record<string, { count: number; total: number }>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  // Modal states
  const [invoiceModalRow, setInvoiceModalRow] = useState<BillingRow | null>(null)
  const [paidModalRow, setPaidModalRow] = useState<BillingRow | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  // Which invoices have their line breakdown expanded. By default
  // invoices with MORE than just a managed_plan line start expanded
  // so the admin notices add-ons without clicking — this is the whole
  // point of Sprint 6 (making Cloudflare/SOCRadar/pentest lines
  // visible from the main Facturación view).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  // Live "users by email domain" breakdown, toggled per invoice row.
  // Kept separate from line-item expansion so the two can be open
  // independently.
  const [domainOpenIds, setDomainOpenIds] = useState<Set<string>>(new Set())
  const toggleDomains = (id: string) => {
    setDomainOpenIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.getBillingPeriod(year, month)
      if (!res.success || !res.data) throw new Error(res.error || 'Error al cargar periodo')
      const payload = (res.data as any).data ?? res.data
      const freshRows: BillingRow[] = payload.rows || []
      setRows(freshRows)
      setTotal(payload.total || 0)
      setByStatus(payload.byStatus || {})
      // Auto-expand invoices that have add-ons (more than just the
      // managed_plan line). The whole point of Sprint 6 is making
      // those visible from the landing view.
      setExpandedIds(new Set(
        freshRows
          .filter(r => (r.lineItems?.length || 0) > 1)
          .map(r => r.id)
      ))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }, [year, month])

  useEffect(() => { load() }, [load])

  const allComplete = rows.length > 0 && rows.every(isRowComplete)
  const incompleteCount = rows.filter(r => !isRowComplete(r)).length
  const periodLabel = `${MONTH_NAMES[month - 1]} ${year}`
  const canSendToFinance = rows.length > 0 && allComplete && !sending

  const handleSendToFinance = async () => {
    setSending(true)
    setSendResult(null)
    try {
      const res = await api.sendBillingToFinance(year, month)
      if (res.success && res.data) {
        const d = res.data as any
        setSendResult({
          type: 'ok',
          text: `Email enviado a ${d.recipient} · ${d.tenantCount} clientes · $${(d.totalUsd || 0).toLocaleString()} USD`,
        })
        await load()  // refresh rows to show new "sent_to_finance" state
      } else {
        // Distinguish "missing fiscal data" from other errors.
        const missing = (res.data as any)?.missingTenants as Array<{ slug: string; name: string }> | undefined
        if (missing && missing.length > 0) {
          setSendResult({
            type: 'err',
            text: `No se envió — faltan datos fiscales en: ${missing.map(m => m.name).join(', ')}`,
          })
        } else {
          setSendResult({ type: 'err', text: res.error || 'No se pudo enviar' })
        }
      }
    } catch (err) {
      setSendResult({ type: 'err', text: err instanceof Error ? err.message : 'Error desconocido' })
    } finally {
      setSending(false)
      setTimeout(() => setSendResult(null), 8000)
    }
  }

  // Sprint 7.1: recompose all pending invoices for the period. Useful
  // when add-ons were created after the snapshot but before sending.
  const [recomposing, setRecomposing] = useState(false)
  const handleRecompose = async () => {
    setRecomposing(true)
    setSendResult(null)
    try {
      const res = await api.recomposeBillingPeriod(year, month)
      if (res.success && res.data) {
        const d = res.data as { rebuilt: number; skipped: number }
        setSendResult({
          type: 'ok',
          text: `Recomposición completada · ${d.rebuilt} facturas refrescadas, ${d.skipped} ya emitidas (intocables)`,
        })
        await load()
      } else {
        setSendResult({ type: 'err', text: res.error || 'No se pudo recomponer' })
      }
    } catch (err) {
      setSendResult({ type: 'err', text: err instanceof Error ? err.message : 'Error' })
    } finally {
      setRecomposing(false)
      setTimeout(() => setSendResult(null), 8000)
    }
  }

  const markRowStatus = async (rowId: string, patch: Parameters<typeof api.updateBillingRow>[1]) => {
    const res = await api.updateBillingRow(rowId, patch)
    if (!res.success) throw new Error(res.error || 'Error al actualizar')
    await load()
  }

  return (
    <div className="space-y-5">
      {/* Period picker + actions row */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-gray-800 rounded-xl p-4 border border-gray-700/50">
        <div className="flex items-center gap-3">
          <label className="text-gray-400 text-sm">Periodo</label>
          <select
            value={month}
            onChange={e => setMonth(parseInt(e.target.value))}
            className="bg-gray-900 border border-gray-700 text-white text-sm rounded px-3 py-1.5 focus:outline-none focus:border-jarix-orange"
          >
            {MONTH_NAMES.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
          </select>
          <select
            value={year}
            onChange={e => setYear(parseInt(e.target.value))}
            className="bg-gray-900 border border-gray-700 text-white text-sm rounded px-3 py-1.5 focus:outline-none focus:border-jarix-orange"
          >
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setPreviewOpen(true)}
            className="px-3 py-1.5 text-sm rounded-lg border border-blue-500/50 text-blue-300 hover:bg-blue-500/10 transition-colors"
            title="Ver cuánto se facturaría si el corte se ejecutara ahora (sin persistir)"
          >
            Vista previa
          </button>
          <button
            onClick={() => {
              if (confirm(`¿Capturar snapshot de ${MONTH_NAMES[month - 1]} ${year}? Consulta JumpCloud en vivo y REEMPLAZA la facturación existente de ese período.`)) {
                onCaptureSnapshot(year, month)
              }
            }}
            disabled={snapshotLoading}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-600 text-gray-200 hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={`Captura los usuarios de JumpCloud y calcula la facturación de ${MONTH_NAMES[month - 1]} ${year} (reemplaza lo existente)`}
          >
            {snapshotLoading ? 'Capturando…' : `Capturar snapshot ${MONTH_NAMES[month - 1]} ${year}`}
          </button>
          {/* Sprint 7.1: refresh pending invoices with current state
              of client_services. No JumpCloud hit — just rebuilds
              line items from what's in DB. Faster than re-snapshot
              and safe on invoices that have already been sent. */}
          <button
            onClick={handleRecompose}
            disabled={recomposing || rows.length === 0}
            className="px-3 py-1.5 text-sm rounded-lg border border-purple-500/50 text-purple-300 hover:bg-purple-500/10 transition-colors disabled:opacity-50"
            title="Recompone las líneas de factura usando el estado actual de client_services (sin tocar JumpCloud). Útil si agregaste add-ons después del snapshot."
          >
            {recomposing ? 'Recomponiendo…' : 'Recomponer líneas'}
          </button>
          <button
            onClick={handleSendToFinance}
            disabled={!canSendToFinance}
            className={`px-4 py-1.5 text-sm rounded-lg font-semibold transition-colors ${
              canSendToFinance
                ? 'bg-jarix-orange hover:bg-jarix-pink text-white'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
            title={
              rows.length === 0
                ? 'Aún no hay snapshot para este periodo'
                : incompleteCount > 0
                ? `${incompleteCount} cliente(s) con datos fiscales incompletos`
                : 'Enviar el reporte mensual a Finanzas'
            }
          >
            {sending ? 'Enviando…' : 'Enviar a Finanzas'}
          </button>
        </div>
      </div>

      {/* Send result banner */}
      {sendResult && (
        <div className={`border rounded-lg px-4 py-3 text-sm ${
          sendResult.type === 'ok'
            ? 'bg-green-500/10 border-green-500/40 text-green-300'
            : 'bg-red-500/10 border-red-500/40 text-red-300'
        }`}>
          {sendResult.text}
        </div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryTile label="Total del periodo" value={`$${total.toLocaleString()}`} accent="green" />
        <SummaryTile label="Clientes" value={String(rows.length)} />
        {(['pending', 'sent_to_finance', 'invoiced', 'paid'] as InvoiceStatus[]).map(s => (
          <SummaryTile
            key={s}
            label={STATUS_META[s].label}
            value={`${byStatus[s]?.count || 0}`}
            sub={byStatus[s] ? `$${byStatus[s].total.toLocaleString()}` : '$0'}
          />
        ))}
      </div>

      {/* Rows */}
      {loading ? (
        <div className="py-12 text-center text-gray-400">Cargando…</div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/40 text-red-300 rounded-lg px-4 py-3">{error}</div>
      ) : rows.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-center text-gray-400">
          <p className="text-lg font-medium text-gray-300 mb-2">No hay snapshot para {periodLabel}</p>
          <p className="text-sm">Presiona <strong>Capturar snapshot</strong> arriba para crear la foto de facturación del mes.</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr className="text-left text-gray-400 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3 text-right">Users</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Factura</th>
                  <th className="px-4 py-3">Vence</th>
                  <th className="px-4 py-3 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700 text-sm">
                {rows.map(r => {
                  const status = (r.invoice_status || 'pending') as InvoiceStatus
                  const meta = STATUS_META[status] || STATUS_META.pending
                  const complete = isRowComplete(r)
                  const lines = r.lineItems || []
                  const addonCount = lines.filter(l => l.lineType !== 'managed_plan').length
                  const isExpanded = expandedIds.has(r.id)
                  return (
                    <React.Fragment key={r.id}>
                    <tr className="hover:bg-gray-700/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.primary_color }} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{r.tenant_name}</span>
                              {/* Line count chip — click to toggle.
                                  Always visible so admin knows there's
                                  detail to see even when collapsed. */}
                              {lines.length > 0 && (
                                <button
                                  onClick={() => toggleExpand(r.id)}
                                  className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${
                                    addonCount > 0
                                      ? 'bg-orange-500/15 text-orange-300 border-orange-500/40 hover:bg-orange-500/25'
                                      : 'bg-gray-700/30 text-gray-400 border-gray-600/40 hover:bg-gray-700/60'
                                  }`}
                                  title={isExpanded ? 'Ocultar líneas' : 'Ver líneas'}
                                >
                                  {isExpanded ? '▾' : '▸'} {lines.length} línea{lines.length === 1 ? '' : 's'}
                                  {addonCount > 0 && ` · +${addonCount} add-on${addonCount === 1 ? '' : 's'}`}
                                </button>
                              )}
                              {/* Domain breakdown — only for managed rows
                                  with a JumpCloud-backed headcount. */}
                              {r.tenant_slug && r.billable_users > 0 && (
                                <button
                                  onClick={() => toggleDomains(r.id)}
                                  className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${
                                    domainOpenIds.has(r.id)
                                      ? 'bg-blue-500/25 text-blue-200 border-blue-500/50'
                                      : 'bg-blue-500/10 text-blue-300 border-blue-500/40 hover:bg-blue-500/20'
                                  }`}
                                  title="Usuarios por dominio de correo (en vivo desde JumpCloud)"
                                >
                                  {domainOpenIds.has(r.id) ? '▾' : '▸'} dominios
                                </button>
                              )}
                            </div>
                            {!complete && (
                              <div className="text-yellow-400 text-xs mt-0.5">⚠ Falta datos fiscales</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-300">{r.billable_users || '—'}</td>
                      <td className="px-4 py-3 text-right text-green-400 font-semibold">
                        ${r.monthly_total.toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${meta.bg} ${meta.text} ${meta.border}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {r.invoice_number ? (
                          <div>
                            <div className="text-white font-mono text-xs">{r.invoice_number}</div>
                            {r.invoice_date && <div className="text-gray-500 text-xs">{r.invoice_date}</div>}
                          </div>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {r.due_date || '—'}
                        {r.paid_date && <div className="text-green-400">Pagada {r.paid_date}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {r.invoice_pdf_url && (
                            <a
                              href={r.invoice_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Abrir PDF de la factura"
                              className="px-2 py-1 text-xs rounded bg-gray-700/40 hover:bg-gray-600/60 text-gray-200 border border-gray-600/50"
                            >
                              PDF
                            </a>
                          )}
                          {status !== 'paid' && status !== 'void' && (
                            <button
                              onClick={() => setInvoiceModalRow(r)}
                              className="px-2 py-1 text-xs rounded bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/40"
                              title="Registrar nº de factura de NetSuite"
                            >
                              Facturada
                            </button>
                          )}
                          {status === 'invoiced' && (
                            <button
                              onClick={() => setPaidModalRow(r)}
                              className="px-2 py-1 text-xs rounded bg-green-500/20 hover:bg-green-500/30 text-green-300 border border-green-500/40"
                            >
                              Pagada
                            </button>
                          )}
                          {status !== 'void' && (
                            <button
                              onClick={async () => {
                                if (!confirm(`¿Anular la factura de ${r.tenant_name} para ${periodLabel}?`)) return
                                try { await markRowStatus(r.id, { invoice_status: 'void' }) } catch (e) { alert(String(e)) }
                              }}
                              className="px-2 py-1 text-xs rounded text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                              title="Anular"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded lines — one row spanning all columns,
                        rendering each invoice_line_item. Visible only
                        when the admin expanded the invoice. */}
                    {isExpanded && lines.length > 0 && (
                      <tr className="bg-gray-900/40">
                        <td colSpan={7} className="px-8 py-2">
                          <div className="space-y-1">
                            {lines.map(l => (
                              <div key={l.id} className="flex items-center gap-3 text-xs py-1 border-b border-gray-700/40 last:border-0">
                                <span
                                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                    l.lineType === 'managed_plan'
                                      ? 'bg-blue-400'
                                      : l.lineType === 'client_service'
                                      ? 'bg-orange-400'
                                      : 'bg-gray-500'
                                  }`}
                                  title={l.lineType}
                                />
                                <span className="flex-1 text-gray-200">{l.description}</span>
                                <span className="text-gray-500 font-mono text-[11px]">
                                  {l.quantity} × ${l.unitPrice.toFixed(2)}
                                  {l.discountPercent > 0 && ` · -${l.discountPercent}%`}
                                </span>
                                <span className="text-gray-200 font-semibold w-24 text-right font-mono">
                                  ${l.lineTotal.toLocaleString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Expanded users-by-domain breakdown. Lazy-fetches
                        live from JumpCloud the first time it's opened. */}
                    {domainOpenIds.has(r.id) && r.tenant_slug && (
                      <tr className="bg-gray-900/40">
                        <td colSpan={7} className="px-8 py-3">
                          <DomainBreakdown tenantSlug={r.tenant_slug} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  )
                })}
              </tbody>
              <tfoot className="bg-gray-900/50 border-t-2 border-gray-600">
                <tr>
                  <td className="px-4 py-3 text-white font-bold">TOTAL</td>
                  <td className="px-4 py-3 text-right text-white font-bold">
                    {rows.reduce((s, r) => s + r.billable_users, 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-green-400 font-bold text-base">
                    ${total.toLocaleString()}
                  </td>
                  <td colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Mark as Invoiced modal */}
      {invoiceModalRow && (
        <InvoiceModal
          row={invoiceModalRow}
          onClose={() => setInvoiceModalRow(null)}
          onSave={async patch => {
            await markRowStatus(invoiceModalRow.id, { ...patch, invoice_status: 'invoiced' })
            setInvoiceModalRow(null)
          }}
        />
      )}

      {/* Mark as Paid modal */}
      {paidModalRow && (
        <PaidModal
          row={paidModalRow}
          onClose={() => setPaidModalRow(null)}
          onSave={async paid_date => {
            await markRowStatus(paidModalRow.id, { invoice_status: 'paid', paid_date })
            setPaidModalRow(null)
          }}
        />
      )}

      {/* Dry-run preview modal */}
      {previewOpen && (
        <PreviewModal
          year={year}
          month={month}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------
// Small presentational components
// ---------------------------------------------------------------

// Live breakdown of a tenant's JumpCloud users grouped by email domain.
// Used as invoice detail so multi-company orgs (e.g. Eron, whose org
// holds astropay.com, eroninternational.com, …) can be shown which
// company each billed user belongs to. Lazy-fetches on mount.
type DomainRow = { domain: string; total: number; activated: number; staged: number; suspended: number }
type DomainData = {
  tenant: { slug: string; name: string; plan: string | null; pricePerUser: number }
  domains: DomainRow[]
  totals: { total: number; activated: number; staged: number; suspended: number }
  amounts: { all: number; activatedOnly: number }
  generatedAt: string
}

function DomainBreakdown({ tenantSlug }: { tenantSlug: string }) {
  const [data, setData] = useState<DomainData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true); setError(null)
    api.getUserDomainBreakdown(tenantSlug)
      .then(res => { if (alive) setData((res as any).data as DomainData) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [tenantSlug])

  const copyTable = () => {
    if (!data) return
    const header = 'Dominio\tTotal\tActivados\tStaged\tSuspendidos'
    const body = data.domains.map(d => `${d.domain}\t${d.total}\t${d.activated}\t${d.staged}\t${d.suspended}`).join('\n')
    const foot = `TOTAL\t${data.totals.total}\t${data.totals.activated}\t${data.totals.staged}\t${data.totals.suspended}`
    navigator.clipboard?.writeText(`${header}\n${body}\n${foot}`)
  }

  if (loading) return <div className="text-gray-400 text-xs py-2">Cargando usuarios desde JumpCloud…</div>
  if (error) return <div className="text-red-400 text-xs py-2">Error: {error}</div>
  if (!data || data.domains.length === 0) return <div className="text-gray-500 text-xs py-2">Sin usuarios.</div>

  const price = data.tenant.pricePerUser
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-blue-300">
          Usuarios por dominio · {data.tenant.name}
          <span className="text-gray-500 normal-case ml-2">${price}/usuario</span>
        </div>
        <button
          onClick={copyTable}
          className="text-[10px] px-2 py-0.5 rounded border border-gray-600/50 bg-gray-700/40 text-gray-300 hover:bg-gray-600/60"
          title="Copiar tabla (pegar en Sheets/Excel)"
        >
          Copiar tabla
        </button>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 border-b border-gray-700/60">
            <th className="text-left font-medium py-1">Dominio</th>
            <th className="text-right font-medium py-1 w-20">Total</th>
            <th className="text-right font-medium py-1 w-20">Activados</th>
            <th className="text-right font-medium py-1 w-20">Staged</th>
            <th className="text-right font-medium py-1 w-24">Suspendidos</th>
          </tr>
        </thead>
        <tbody>
          {data.domains.map(d => (
            <tr key={d.domain} className="border-b border-gray-800/60">
              <td className="py-1 text-gray-200 font-mono">{d.domain}</td>
              <td className="py-1 text-right text-white font-semibold">{d.total}</td>
              <td className="py-1 text-right text-green-400">{d.activated}</td>
              <td className="py-1 text-right text-yellow-400">{d.staged || '—'}</td>
              <td className="py-1 text-right text-gray-400">{d.suspended || '—'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-gray-600 font-bold text-white">
            <td className="py-1.5">TOTAL</td>
            <td className="py-1.5 text-right">{data.totals.total}</td>
            <td className="py-1.5 text-right text-green-400">{data.totals.activated}</td>
            <td className="py-1.5 text-right text-yellow-400">{data.totals.staged}</td>
            <td className="py-1.5 text-right text-gray-400">{data.totals.suspended}</td>
          </tr>
        </tfoot>
      </table>
      <div className="flex flex-wrap gap-4 text-[11px] text-gray-400 pt-1">
        <span>Facturando <b className="text-white">todos</b> ({data.totals.total}): <b className="text-green-400">${data.amounts.all.toLocaleString()}</b></span>
        <span>Solo <b className="text-white">activados</b> ({data.totals.activated}): <b className="text-green-400">${data.amounts.activatedOnly.toLocaleString()}</b></span>
        <span className="text-gray-600">Actualizado {new Date(data.generatedAt).toLocaleString()}</span>
      </div>
    </div>
  )
}

function SummaryTile({ label, value, sub, accent }: {
  label: string; value: string; sub?: string; accent?: 'green'
}) {
  const valueClass = accent === 'green' ? 'text-green-400' : 'text-white'
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className={`text-xl font-bold ${valueClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  )
}

// ---------------------------------------------------------------
// Modals
// ---------------------------------------------------------------

function InvoiceModal({ row, onClose, onSave }: {
  row: BillingRow
  onClose: () => void
  onSave: (patch: { invoice_number: string; invoice_date: string; purchase_order?: string; invoice_pdf_url?: string | null }) => Promise<void>
}) {
  const [invoiceNumber, setInvoiceNumber] = useState(row.invoice_number || '')
  const [invoiceDate, setInvoiceDate] = useState(row.invoice_date || new Date().toISOString().split('T')[0])
  const [purchaseOrder, setPurchaseOrder] = useState(row.purchase_order || '')
  const [invoicePdfUrl, setInvoicePdfUrl] = useState(row.invoice_pdf_url || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Lightweight URL validation: either empty (clears field) or a real
  // https URL. The worker enforces this too, but showing the user
  // feedback before they hit "Confirmar" is kinder.
  const trimmedPdf = invoicePdfUrl.trim()
  const pdfUrlValid = trimmedPdf === '' || (() => {
    try {
      const u = new URL(trimmedPdf)
      return u.protocol === 'https:'
    } catch { return false }
  })()

  const canSave = invoiceNumber.trim().length > 0 && invoiceDate.length === 10 && pdfUrlValid

  const save = async () => {
    if (!canSave) return
    setSaving(true)
    setErr(null)
    try {
      await onSave({
        invoice_number: invoiceNumber.trim(),
        invoice_date: invoiceDate,
        purchase_order: purchaseOrder.trim() || undefined,
        // null on empty string so the backend can clear a previously-set link.
        invoice_pdf_url: trimmedPdf || null,
      })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Error')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="border-b border-gray-700 px-5 py-3">
          <h3 className="text-white font-semibold">Marcar como facturada — {row.tenant_name}</h3>
          <p className="text-gray-500 text-xs mt-1">Pega el número de factura emitido en NetSuite.</p>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Nº de factura NetSuite *">
            <input
              type="text"
              autoFocus
              value={invoiceNumber}
              onChange={e => setInvoiceNumber(e.target.value)}
              placeholder="INV-0001234"
              className="w-full bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-jarix-orange font-mono"
            />
          </Field>
          <Field label="Fecha de emisión *">
            <input
              type="date"
              value={invoiceDate}
              onChange={e => setInvoiceDate(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-jarix-orange"
            />
          </Field>
          <Field label={`Orden de compra del cliente (opcional) · ${row.payment_terms}`}>
            <input
              type="text"
              value={purchaseOrder}
              onChange={e => setPurchaseOrder(e.target.value)}
              placeholder="PO-…"
              className="w-full bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-jarix-orange"
            />
          </Field>
          <Field label="Link al PDF (opcional) · Drive, Dropbox, NetSuite">
            <input
              type="url"
              value={invoicePdfUrl}
              onChange={e => setInvoicePdfUrl(e.target.value)}
              placeholder="https://drive.google.com/..."
              className={`w-full bg-gray-800 border text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-jarix-orange ${
                pdfUrlValid ? 'border-gray-700' : 'border-red-500/60'
              }`}
            />
            {!pdfUrlValid && (
              <span className="block text-red-400 text-[11px] mt-1">
                Debe ser una URL https:// válida
              </span>
            )}
            <span className="block text-gray-500 text-[11px] mt-1">
              Una vez guardado, el cliente verá el botón "Descargar PDF" en su portal.
            </span>
          </Field>
          {err && <div className="text-red-400 text-xs">{err}</div>}
        </div>
        <div className="border-t border-gray-700 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-800" disabled={saving}>
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={!canSave || saving}
            className={`px-4 py-1.5 text-sm rounded font-semibold ${
              canSave && !saving ? 'bg-purple-500 hover:bg-purple-600 text-white' : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? 'Guardando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}

function PaidModal({ row, onClose, onSave }: {
  row: BillingRow
  onClose: () => void
  onSave: (paid_date: string) => Promise<void>
}) {
  const [paidDate, setPaidDate] = useState(row.paid_date || new Date().toISOString().split('T')[0])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setSaving(true)
    setErr(null)
    try { await onSave(paidDate) } catch (e) {
      setErr(e instanceof Error ? e.message : 'Error')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="border-b border-gray-700 px-5 py-3">
          <h3 className="text-white font-semibold">Marcar como pagada — {row.tenant_name}</h3>
          <p className="text-gray-500 text-xs mt-1">
            Factura <span className="font-mono">{row.invoice_number}</span> · ${row.monthly_total.toLocaleString()} USD
          </p>
        </div>
        <div className="p-5">
          <Field label="Fecha de pago">
            <input
              type="date"
              autoFocus
              value={paidDate}
              onChange={e => setPaidDate(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-jarix-orange"
            />
          </Field>
          {err && <div className="text-red-400 text-xs mt-2">{err}</div>}
        </div>
        <div className="border-t border-gray-700 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-800" disabled={saving}>
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-1.5 text-sm rounded font-semibold bg-green-500 hover:bg-green-600 text-white disabled:opacity-50"
          >
            {saving ? 'Guardando…' : 'Confirmar pago'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-gray-400 text-xs font-medium mb-1">{label}</span>
      {children}
    </label>
  )
}

// ---------------------------------------------------------------
// Preview modal — shows the day-15 dry-run
// ---------------------------------------------------------------

interface PreviewRow {
  tenant_id: string
  tenant_name: string
  tenant_slug: string
  primary_color: string
  plan: string
  price_per_user: number
  previous_users: number | null
  current_users: number
  previous_total: number | null
  current_total: number
  /** Sprint 6: count + $ of non-managed_plan lines on this invoice. */
  addons_count: number
  addons_total: number
  /** The actual composed lines (what the cron will persist). */
  line_items: Array<{
    lineType: string
    description: string
    quantity: number
    unitPrice: number
    discountPercent: number
    lineTotal: number
  }>
  delta_users: number | null
  delta_total: number | null
  prorated_count: number
  proration_enabled: boolean
  error?: string
}

function PreviewModal({ year, month, onClose }: {
  year: number; month: number; onClose: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<PreviewRow[]>([])
  const [totals, setTotals] = useState({ current: 0, previous: 0, delta: 0, deltaPct: null as number | null })
  const [prevPeriod, setPrevPeriod] = useState<{ year: number; month: number; complete: boolean } | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api.getBillingPreview(year, month)
      .then(res => {
        if (cancelled) return
        if (!res.success || !res.data) throw new Error(res.error || 'Error al cargar preview')
        const payload = (res.data as any).data ?? res.data
        setRows(payload.rows || [])
        setTotals(payload.totals)
        setPrevPeriod(payload.previousPeriod)
      })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Error') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [year, month])

  const periodLabel = `${MONTH_NAMES[month - 1]} ${year}`
  const prevLabel = prevPeriod ? `${MONTH_NAMES[prevPeriod.month - 1]} ${prevPeriod.year}` : '—'

  const deltaColor = totals.delta > 0 ? 'text-green-400' : totals.delta < 0 ? 'text-red-400' : 'text-gray-400'
  const deltaSign = totals.delta > 0 ? '+' : ''

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold text-lg">Vista previa — {periodLabel}</h3>
            <p className="text-gray-400 text-sm mt-0.5">
              Simulación del corte en tiempo real · diff vs {prevLabel}{prevPeriod && !prevPeriod.complete ? ' (parcial)' : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2" aria-label="Cerrar">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center text-gray-400">Calculando…</div>
          ) : error ? (
            <div className="mx-6 my-4 bg-red-500/10 border border-red-500/40 text-red-300 rounded px-4 py-3">{error}</div>
          ) : (
            <>
              {/* Summary bar */}
              <div className="mx-6 mt-4 grid grid-cols-3 gap-3">
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Total anterior</div>
                  <div className="text-xl font-bold text-gray-300">${totals.previous.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{prevLabel}</div>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Total este periodo</div>
                  <div className="text-xl font-bold text-green-400">${totals.current.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Si se cortara ahora</div>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Variación</div>
                  <div className={`text-xl font-bold ${deltaColor}`}>
                    {deltaSign}${Math.abs(totals.delta).toLocaleString()}
                  </div>
                  <div className={`text-xs mt-0.5 ${deltaColor}`}>
                    {totals.deltaPct !== null ? `${deltaSign}${totals.deltaPct}%` : '—'}
                  </div>
                </div>
              </div>

              {/* Rows table */}
              <table className="w-full mt-4 text-sm">
                <thead className="bg-gray-800/50 text-xs uppercase tracking-wider text-gray-400">
                  <tr>
                    <th className="text-left px-6 py-2">Cliente</th>
                    <th className="text-right px-4 py-2">Users ant.</th>
                    <th className="text-right px-4 py-2">Users ahora</th>
                    <th className="text-right px-4 py-2">Δ Users</th>
                    <th className="text-right px-4 py-2">Total ant.</th>
                    <th className="text-right px-4 py-2">Total ahora</th>
                    <th className="text-right px-4 py-2">Δ $</th>
                    <th className="text-center px-4 py-2">Notas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {rows.map(r => {
                    const dollarDelta = r.delta_total
                    const userDelta = r.delta_users
                    const deltaDollarClass = dollarDelta == null ? 'text-gray-500'
                      : dollarDelta > 0 ? 'text-green-400'
                      : dollarDelta < 0 ? 'text-red-400' : 'text-gray-400'
                    const deltaUserClass = userDelta == null ? 'text-gray-500'
                      : userDelta > 0 ? 'text-green-400'
                      : userDelta < 0 ? 'text-red-400' : 'text-gray-400'
                    return (
                      <tr key={r.tenant_id} className="hover:bg-gray-800/40">
                        <td className="px-6 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: r.primary_color }} />
                            <span className="text-white font-medium">{r.tenant_name}</span>
                          </div>
                          {r.error && <div className="text-red-400 text-xs mt-0.5">⚠ {r.error}</div>}
                        </td>
                        <td className="text-right px-4 py-2 text-gray-400">{r.previous_users ?? '—'}</td>
                        <td className="text-right px-4 py-2 text-white">{r.current_users}</td>
                        <td className={`text-right px-4 py-2 font-medium ${deltaUserClass}`}>
                          {userDelta == null ? '—' : (userDelta > 0 ? `+${userDelta}` : String(userDelta))}
                        </td>
                        <td className="text-right px-4 py-2 text-gray-400">
                          {r.previous_total != null ? `$${r.previous_total.toLocaleString()}` : '—'}
                        </td>
                        <td className="text-right px-4 py-2 text-green-400 font-semibold">
                          ${r.current_total.toLocaleString()}
                        </td>
                        <td className={`text-right px-4 py-2 font-medium ${deltaDollarClass}`}>
                          {dollarDelta == null ? '—' : (dollarDelta > 0 ? `+$${dollarDelta.toLocaleString()}` : `-$${Math.abs(dollarDelta).toLocaleString()}`)}
                        </td>
                        <td className="text-center px-4 py-2 text-xs">
                          {r.prorated_count > 0 && (
                            <span className="inline-block bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded px-1.5 py-0.5">
                              {r.prorated_count} pro-rata
                            </span>
                          )}
                          {r.proration_enabled && r.prorated_count === 0 && (
                            <span className="text-gray-500" title="Tenant con pro-rata activada, sin usuarios nuevos este mes">·</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-700 flex justify-between items-center">
          <p className="text-gray-500 text-xs">
            Esta vista no persiste datos — para dejarlo como corte oficial, usa <strong>Capturar snapshot</strong>.
          </p>
          <button onClick={onClose} className="px-4 py-1.5 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}
